import {useCallback, useMemo} from 'react';
import {shallowEqual} from 'react-redux';
import {useAppDispatch, useAppSelector} from '~/QuickEcards/store';
import {selectAssets} from '~/QuickEcards/store/reducers/assets';
import {
  selectEcardId,
  selectSelectedAsset,
  updateEventDesignOptions,
} from '~/QuickEcards/store/reducers/ecard';
import {Asset} from '~/QuickEcards/types/ecard';
import {OverlayIcon} from '~/QuickEcards/types/misc';
import {logChangeEffect, logChangeReveal} from '~/QuickEcards/utils/logging';

interface SelectedOverlay extends Asset {
  srcVp9?: string;
  srcH265?: string;
  iconVp9?: string;
  iconH265?: string;
  isVideo: boolean;
}

interface UseOverlayReturn {
  onOverlayChange: (id: string) => void;
  onOverlayClear: () => void;
  overlayIcons: OverlayIcon[];
  selectedOverlay?: SelectedOverlay;
}

export const useOverlay = (
  isMobile: boolean = false,
  isReveal: boolean = false
): UseOverlayReturn => {
  const dispatch = useAppDispatch();
  const ecardId = useAppSelector(selectEcardId);
  const {reveals, overlays: retrievedOverlays} = useAppSelector(selectAssets, shallowEqual);
  const overlayId = useAppSelector((state) =>
    selectSelectedAsset(state, isReveal ? 'reveals' : 'effects')
  );
  const overlays = useMemo<Asset[]>(
    () => (isReveal ? reveals : retrievedOverlays),
    [isReveal, retrievedOverlays, reveals]
  );

  const overlayIcons: OverlayIcon[] = useMemo(
    () =>
      overlays.map((overlay) => {
        const iconVp9 = overlay.media.find(({label}) => label === 'icon_vp9')?.url || '';
        return {
          iconVp9,
          defaultSrc: iconVp9,
          iconH265: overlay?.media.find(({label}) => label === 'icon_h265')?.url || '',
          id: overlay.id,
        };
      }),
    [overlays]
  );

  const onOverlayClear = useCallback(() => {
    if (isReveal) {
      // Reveal
      dispatch(
        updateEventDesignOptions({
          // biome-ignore lint/style/useNamingConvention: existing error
          reveal_id: '',
          // biome-ignore lint/style/useNamingConvention: existing error
          reveal_video_desktop_vp9: '',
          // biome-ignore lint/style/useNamingConvention: existing error
          reveal_video_mobile_vp9: '',
          // biome-ignore lint/style/useNamingConvention: existing error
          reveal_video_desktop_h265: '',
          // biome-ignore lint/style/useNamingConvention: existing error
          reveal_video_mobile_h265: '',
        })
      );
      logChangeReveal(ecardId, '');
    } else {
      // Effect
      dispatch(
        updateEventDesignOptions({
          // biome-ignore lint/style/useNamingConvention: existing error
          overlay_id: '',
          // biome-ignore lint/style/useNamingConvention: existing error
          overlay_video_desktop_vp9: '',
          // biome-ignore lint/style/useNamingConvention: existing error
          overlay_video_mobile_vp9: '',
          // biome-ignore lint/style/useNamingConvention: existing error
          overlay_video_desktop_h265: '',
          // biome-ignore lint/style/useNamingConvention: existing error
          overlay_video_mobile_h265: '',
        })
      );
      logChangeEffect(ecardId, '');
    }
  }, [isReveal, dispatch, ecardId]);

  const onOverlayChange = useCallback(
    (id: string) => {
      const overlay = overlays.find((o) => o.id === id);
      if (!overlay) return;

      const desktopVp9 = overlay.media.find(({label}) => label === 'desktop_vp9');
      const desktopH265 = overlay.media.find(({label}) => label === 'desktop_h265');
      const mobileVp9 = overlay.media.find(({label}) => label === 'mobile_vp9');
      const mobileH265 = overlay.media.find(({label}) => label === 'mobile_h265');
      if (isReveal) {
        // Reveal
        dispatch(
          updateEventDesignOptions({
            // biome-ignore lint/style/useNamingConvention: existing error
            reveal_id: id,
            // biome-ignore lint/style/useNamingConvention: existing error
            reveal_video_desktop_vp9: desktopVp9?.url,
            // biome-ignore lint/style/useNamingConvention: existing error
            reveal_video_mobile_vp9: mobileVp9?.url,
            // biome-ignore lint/style/useNamingConvention: existing error
            reveal_video_desktop_h265: desktopH265?.url,
            // biome-ignore lint/style/useNamingConvention: existing error
            reveal_video_mobile_h265: mobileH265?.url,
          })
        );
        logChangeReveal(ecardId, id);
      } else {
        // Effect
        dispatch(
          updateEventDesignOptions({
            // biome-ignore lint/style/useNamingConvention: existing error
            overlay_id: id,
            // biome-ignore lint/style/useNamingConvention: existing error
            overlay_video_desktop_vp9: desktopVp9?.url,
            // biome-ignore lint/style/useNamingConvention: existing error
            overlay_video_mobile_vp9: mobileVp9?.url,
            // biome-ignore lint/style/useNamingConvention: existing error
            overlay_video_desktop_h265: desktopH265?.url,
            // biome-ignore lint/style/useNamingConvention: existing error
            overlay_video_mobile_h265: mobileH265?.url,
          })
        );
        logChangeEffect(ecardId, id);
      }
    },
    [dispatch, isReveal, overlays, ecardId]
  );

  const selectedOverlay = useMemo(() => {
    const selected = overlays.find(({id}) => id === overlayId);
    if (!selected) return undefined;

    const mode = isMobile ? 'mobile' : 'desktop';
    const srcVp9 = selected.media.find(({label}) => label === `${mode}_vp9`)?.url;
    const iconVp9 = selected.media.find(({label}) => label === 'icon_vp9')?.url;
    const srcH265 = selected.media.find(({label}) => label === `${mode}_h265`)?.url;
    const iconH265 = selected.media.find(({label}) => label === 'icon_h265')?.url;

    return {
      ...selected,
      srcVp9,
      srcH265,
      iconVp9,
      iconH265,
      isVideo: true,
      id: overlayId,
    } as SelectedOverlay;
  }, [overlays, overlayId, isMobile]);

  return {onOverlayChange, onOverlayClear, overlayIcons, selectedOverlay};
};
